import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Divider,
  Alert,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  GetApp as GetAppIcon,
  CloudDownload as CloudDownloadIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Handshake as HandshakeIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { importExportApi } from '../api/importExportApi';
import type { ImportResult, ExportStatus } from '../api/importExportApi';

interface ImportExportPanelProps {
  onImportComplete?: (result: ImportResult) => void;
}

export const ImportExportPanel: React.FC<ImportExportPanelProps> = ({
  onImportComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedEntityType, setSelectedEntityType] = useState<'contacts' | 'deals' | 'accounts'>('contacts');

  // Get system status
  const { data: status, isLoading: statusLoading } = useQuery<ExportStatus>({
    queryKey: ['import-export-status'],
    queryFn: () => importExportApi.getStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const handleExport = async (type: 'contacts' | 'deals' | 'accounts' | 'all') => {
    setLoading(true);
    try {
      switch (type) {
        case 'contacts':
          await importExportApi.exportAndDownloadContacts();
          break;
        case 'deals':
          await importExportApi.exportAndDownloadDeals();
          break;
        case 'accounts':
          await importExportApi.exportAndDownloadAccounts();
          break;
        case 'all':
          await importExportApi.exportAndDownloadAllData();
          break;
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async (entityType: 'contacts' | 'deals' | 'accounts') => {
    try {
      await importExportApi.downloadImportTemplate(entityType);
    } catch (error) {
      console.error('Template download failed:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      const result = await importExportApi.importContactsWithValidation(file);
      setImportResult(result);
      setImportDialogOpen(false);
      onImportComplete?.(result);
    } catch (error) {
      setImportResult({
        message: 'Import failed',
        imported_count: 0,
        total_rows: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error']
      });
    } finally {
      setLoading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const exportOptions = [
    {
      type: 'contacts' as const,
      title: 'Export Contacts',
      description: 'Download all contact data as CSV',
      icon: <PeopleIcon />,
      count: status?.data_counts.contacts || 0
    },
    {
      type: 'deals' as const,
      title: 'Export Deals',
      description: 'Download all deal data as CSV',
      icon: <HandshakeIcon />,
      count: status?.data_counts.deals || 0
    },
    {
      type: 'accounts' as const,
      title: 'Export Accounts',
      description: 'Download all account data as CSV',
      icon: <BusinessIcon />,
      count: status?.data_counts.accounts || 0
    }
  ];

  return (
    <Box>
      {/* System Status */}
      {status && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {status.data_counts.contacts + status.data_counts.deals + status.data_counts.accounts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Records
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {status.recent_activity.contacts_last_7_days + status.recent_activity.deals_last_7_days}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Added This Week
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip
                    label={status.status}
                    color="success"
                    variant="outlined"
                    icon={<CheckCircleIcon />}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    System Health
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Export Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Export Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download your CRM data for backup, analysis, or migration to other systems.
          </Typography>

          <Grid container spacing={2}>
            {exportOptions.map((option) => (
              <Grid item xs={12} sm={6} md={4} key={option.type}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {option.icon}
                      <Typography variant="subtitle1" sx={{ ml: 1 }}>
                        {option.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {option.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={`${option.count} records`}
                        size="small"
                        variant="outlined"
                      />
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleExport(option.type)}
                        disabled={loading || option.count === 0}
                      >
                        Export
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Button
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            onClick={() => handleExport('all')}
            disabled={loading}
            size="large"
          >
            Export All Data (ZIP)
          </Button>
        </CardContent>
      </Card>

      {/* Import Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Import Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Import contacts, deals, or accounts from CSV files. Perfect for migrating from other systems.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() => handleDownloadTemplate('contacts')}
                fullWidth
              >
                Download Contact Template
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => setImportDialogOpen(true)}
                disabled={loading}
                fullWidth
              >
                Import Contacts
              </Button>
            </Grid>
          </Grid>

          {status?.features && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Supported Features:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {status.features.map((feature, index) => (
                  <Chip key={index} label={feature} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Loading Progress */}
      {loading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Processing...
          </Typography>
        </Box>
      )}

      {/* Import Result */}
      {importResult && (
        <Alert
          severity={importResult.errors.length > 0 ? 'warning' : 'success'}
          sx={{ mt: 2 }}
          onClose={() => setImportResult(null)}
        >
          <Typography variant="subtitle2">
            {importResult.message}
          </Typography>
          <Typography variant="body2">
            Imported {importResult.imported_count} of {importResult.total_rows} records
          </Typography>
          {importResult.errors.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="error">
                {importResult.errors.length} errors occurred:
              </Typography>
              <List dense>
                {importResult.errors.slice(0, 5).map((error, index) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <ErrorIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={error}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
                {importResult.errors.length > 5 && (
                  <Typography variant="body2" color="text.secondary">
                    ... and {importResult.errors.length - 5} more errors
                  </Typography>
                )}
              </List>
            </Box>
          )}
        </Alert>
      )}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Contacts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a CSV file to import contacts. Make sure your file follows the template format.
          </Typography>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="csv-upload"
          />
          <label htmlFor="csv-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadIcon />}
              fullWidth
              sx={{ mb: 2 }}
            >
              Choose CSV File
            </Button>
          </label>

          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              <strong>Tips for successful import:</strong>
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Use the template format</li>
              <li>Email field is required</li>
              <li>File size limit: 10MB</li>
              <li>Duplicate emails will be skipped</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleDownloadTemplate('contacts')} startIcon={<DescriptionIcon />}>
            Download Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportExportPanel;
